// src/views/ContratoForm.jsx
import React, { useState } from "react";
import { createContrato, updateContrato } from "../services/api";
import { useNavigate, useParams } from "react-router-dom";
import { OFICINAS_POR_MUNICIPIO } from "../catalogos/oficinasPorMunicipio";

export default function ContratoForm() {
  const navigate = useNavigate();
  const { id } = useParams();

  // -----------------------------
  // MÁSCARA TELÉFONO
  // -----------------------------
  const formatPhone = (value) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 6);
    const p3 = digits.slice(6, 10);

    if (digits.length <= 3) return `(${p1}`;
    if (digits.length <= 6) return `(${p1}) ${p2}`;
    return `(${p1}) ${p2}-${p3}`;
  };

  // -----------------------------
  // MÁSCARA MONEDA MX
  // -----------------------------
  const formatCurrency = (value) => {
    if (!value) return "";
    const digits = value.replace(/[^\d]/g, "");
    if (!digits) return "";
    const number = Number(digits) / 100;
    return number.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
    });
  };

  const limpiarTelefono = (t) => t.replace(/\D/g, "");

  // 🔒 FIX DEFINITIVO (Starlink)
  const limpiarMonto = (m) => {
    if (!m || typeof m !== "string") return null;
    const digits = m.replace(/[^\d]/g, "");
    if (!digits) return null;
    return Number(digits) / 100;
  };

  // -----------------------------
  // FORMULARIO
  // -----------------------------
  const COMPANIAS = ["Telmex", "Totalplay", "Starlink"];

  const [form, setForm] = useState({
    compania: "",
    ubicacion: "",
    oficina: "",
    propietario: "",
    correo: "",
    telefono: "",
    fechaPago: "",
    totalPagar: "",
    numeroCuenta: "",
    contrasena: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const mostrarCampos = {
    Telmex: ["ubicacion", "propietario", "oficina", "correo", "telefono", "totalPagar"],
    Totalplay: ["ubicacion", "propietario", "oficina", "numeroCuenta", "contrasena", "totalPagar"],
    Starlink: ["ubicacion", "propietario", "oficina", "correo", "totalPagar"],
  };

  const camposVisibles = mostrarCampos[form.compania] || [];

  const MUNICIPIOS_QROO = Object.keys(OFICINAS_POR_MUNICIPIO);
  const oficinasDisponibles = OFICINAS_POR_MUNICIPIO[form.ubicacion] || [];

  // -----------------------------
  // TOASTS
  // -----------------------------
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };

  const cerrarToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // -----------------------------
  // GUARDAR
  // -----------------------------
  const guardar = async () => {
    try {
      if (!form.compania) return showToast("Seleccione una compañía", "error");
      if (!form.ubicacion) return showToast("Seleccione una ubicación", "error");

      if (camposVisibles.includes("totalPagar")) {
        const monto = limpiarMonto(form.totalPagar);
        if (monto === null || monto <= 0) {
          return showToast("Capture un total a pagar válido", "error");
        }
      }

      let payload = {};

      camposVisibles.forEach((campo) => {
        if (campo === "telefono") {
          payload.telefono = limpiarTelefono(form.telefono);
        } else if (campo === "totalPagar") {
          const monto = limpiarMonto(form.totalPagar);
          if (monto !== null) payload.totalPagar = monto;
        } else {
          payload[campo] = form[campo];
        }
      });

      payload.compania = form.compania;
      payload.fechaPago = form.fechaPago || "";

      if (id) {
        await updateContrato(id, payload);
        showToast("Contrato actualizado correctamente", "success");
      } else {
        await createContrato(payload);
        showToast("Contrato guardado correctamente", "success");
      }

      setTimeout(() => navigate("/contratos"), 900);
    } catch (err) {
      console.error("❌ Error al guardar contrato:", err);
      showToast("Error al guardar contrato", "error");
    }
  };

  return (
    <>
      {/* TOASTS */}
      <div className="fixed top-4 right-4 z-50 space-y-3">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`px-4 py-3 rounded-xl shadow-lg text-white flex justify-between ${
              t.type === "success" ? "bg-green-600" : "bg-red-600"
            }`}
            style={{ minWidth: 260 }}
          >
            <span>{t.message}</span>
            <button onClick={() => cerrarToast(t.id)}>×</button>
          </div>
        ))}
      </div>

      {/* FORMULARIO */}
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Agregar Contrato</h1>
        <p className="text-gray-500 mb-6">
          Captura la información del contrato de servicio.
        </p>

        {/* COMPAÑÍA */}
        <div className="bg-white p-6 rounded-xl shadow-sm border mb-6">
          <label className="font-semibold text-sm">Compañía *</label>
          <select
            name="compania"
            value={form.compania}
            onChange={handleChange}
            className="w-full mt-2 p-2 border rounded-lg bg-gray-50"
          >
            <option value="">Seleccione compañía</option>
            {COMPANIAS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {form.compania && (
          <>
            {/* DATOS GENERALES */}
            <div className="bg-white p-6 rounded-xl shadow-sm border mb-6">
              <h2 className="text-lg font-semibold mb-3">Datos generales</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {camposVisibles.includes("ubicacion") && (
                  <div>
                    <label className="text-sm font-medium">Ubicación *</label>
                    <select
                      name="ubicacion"
                      value={form.ubicacion}
                      onChange={handleChange}
                      className="w-full mt-1 p-2 border rounded-lg bg-gray-50"
                    >
                      <option value="">Seleccione municipio</option>
                      {MUNICIPIOS_QROO.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                )}

                {camposVisibles.includes("oficina") && (
                  <div>
                    <label className="text-sm font-medium">Oficina *</label>
                    <select
                      name="oficina"
                      value={form.oficina}
                      onChange={handleChange}
                      disabled={!form.ubicacion}
                      className="w-full mt-1 p-2 border rounded-lg bg-gray-50"
                    >
                      <option value="">Seleccione oficina</option>
                      {oficinasDisponibles.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                )}

                {camposVisibles.includes("propietario") && (
                  <div>
                    <label className="text-sm font-medium">Propietario *</label>
                    <input
                      name="propietario"
                      value={form.propietario}
                      onChange={handleChange}
                      className="w-full mt-1 p-2 border rounded-lg bg-gray-50"
                    />
                  </div>
                )}

                {camposVisibles.includes("correo") && (
                  <div>
                    <label className="text-sm font-medium">Correo *</label>
                    <input
                      type="email"
                      name="correo"
                      value={form.correo}
                      onChange={handleChange}
                      className="w-full mt-1 p-2 border rounded-lg bg-gray-50"
                    />
                  </div>
                )}

                {camposVisibles.includes("telefono") && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium">Teléfono *</label>
                    <input
                      name="telefono"
                      value={form.telefono}
                      onChange={(e) =>
                        setForm({ ...form, telefono: formatPhone(e.target.value) })
                      }
                      maxLength={16}
                      placeholder="(999) 999-9999"
                      className="w-full mt-1 p-2 border rounded-lg bg-gray-50"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* DETALLES */}
            <div className="bg-white p-6 rounded-xl shadow-sm border mb-6">
              <h2 className="text-lg font-semibold mb-3">Detalles del contrato</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {camposVisibles.includes("totalPagar") && (
                  <div>
                    <label className="text-sm font-medium">Total a pagar *</label>
                    <input
                      name="totalPagar"
                      value={form.totalPagar}
                      onChange={(e) =>
                        setForm({ ...form, totalPagar: formatCurrency(e.target.value) })
                      }
                      placeholder="$0.00"
                      className="w-full mt-1 p-2 border rounded-lg bg-gray-50 text-right"
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium">Fecha de pago</label>
                  <input
                    type="date"
                    name="fechaPago"
                    value={form.fechaPago}
                    onChange={handleChange}
                    className="w-full mt-1 p-2 border rounded-lg bg-gray-50"
                  />
                </div>

                {camposVisibles.includes("numeroCuenta") && (
                  <div>
                    <label className="text-sm font-medium">Número de cuenta</label>
                    <input
                      name="numeroCuenta"
                      value={form.numeroCuenta}
                      onChange={handleChange}
                      className="w-full mt-1 p-2 border rounded-lg bg-gray-50"
                    />
                  </div>
                )}

                {camposVisibles.includes("contrasena") && (
                  <div>
                    <label className="text-sm font-medium">Contraseña *</label>
                    <input
                      name="contrasena"
                      value={form.contrasena}
                      onChange={handleChange}
                      className="w-full mt-1 p-2 border rounded-lg bg-gray-50"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* BOTONES */}
        <div className="flex gap-4 justify-end">
          <button
            className="px-6 py-2 rounded-xl bg-gray-300"
            onClick={() => navigate("/contratos")}
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={guardar}
            className="px-6 py-2 rounded-xl bg-blue-600 text-white font-semibold"
          >
            Guardar contrato
          </button>
        </div>
      </div>
    </>
  );
}
